/**
 * Aparência do widget: o que o painel desenha na prévia e o widget desenha no
 * site do cliente.
 *
 * Vive aqui porque os dois precisam do mesmo resultado. O painel não pode
 * importar o widget, que traz Preact e os ganchos de console e rede junto, e
 * copiar as medidas para os dois lados foi o que já produziu prévia divergindo
 * do produto. Este módulo é puro: sem framework, sem DOM, sem CSS.
 */

export type LauncherShape = 'pill' | 'rounded' | 'square' | 'circle';
export type LauncherSize = 'small' | 'medium' | 'large';

export interface LauncherCorners {
  top_left: boolean;
  top_right: boolean;
  bottom_right: boolean;
  bottom_left: boolean;
}

export interface LauncherMetrics {
  padding: string;
  font: string;
  icon: string;
  circle: string;
}

/** Medidas de cada tamanho: o que muda entre eles é só isto. */
export const LAUNCHER_SIZES: Record<LauncherSize, LauncherMetrics> = {
  small: { padding: '9px 14px', font: '13px', icon: '16px', circle: '40px' },
  medium: { padding: '12px 18px', font: '14px', icon: '18px', circle: '48px' },
  large: { padding: '15px 24px', font: '16px', icon: '21px', circle: '58px' },
};

/** Raio de cada formato. O círculo usa metade do lado. */
export const LAUNCHER_SHAPE_RADIUS: Record<LauncherShape, string> = {
  pill: '999px',
  rounded: '14px',
  square: '0px',
  circle: '50%',
};

/**
 * Raio por canto, na ordem que o CSS espera.
 *
 * Canto desmarcado fica reto. É o que permite encostar o botão na quina da tela
 * sem ele parecer solto: no canto inferior direito, costuma ficar melhor com só
 * o canto superior esquerdo arredondado.
 */
export function launcherRadius(shape: LauncherShape, corners: LauncherCorners): string {
  const raio = LAUNCHER_SHAPE_RADIUS[shape];
  return [corners.top_left, corners.top_right, corners.bottom_right, corners.bottom_left]
    .map((arredonda) => (arredonda ? raio : '0'))
    .join(' ');
}

/**
 * Tons do painel do widget em cada tema.
 *
 * O widget os escreve no CSS do Shadow DOM e o painel os usa na prévia. Ficam
 * aqui pelo mesmo motivo das medidas: mantidos em dois lugares, a prévia
 * mostraria uma coisa e o produto outra.
 */
export const WIDGET_LIGHT_SURFACE = '#ffffff';
export const WIDGET_DARK_SURFACE = '#201f2b';

export const WIDGET_LIGHT_INK = '#1c1b26';
export const WIDGET_DARK_INK = '#f2f1f7';
export const WIDGET_LIGHT_MUTED = '#6b6879';
export const WIDGET_DARK_MUTED = '#a29fb3';
export const WIDGET_LIGHT_BORDER = '#e6e3f0';
export const WIDGET_DARK_BORDER = '#35334a';

const AA_NORMAL_TEXT = 4.5;

/** Igual ao texto escuro do painel do widget. */
const DARK_INK = WIDGET_LIGHT_INK;
const LIGHT_INK = '#ffffff';

/** Passo da busca pela versão legível. Menor que isto muda a cor sem necessidade. */
const MIX_STEP = 0.05;

type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match?.[1]) {
    throw new Error(`Cor fora do formato #rrggbb: ${hex}`);
  }
  const value = Number.parseInt(match[1], 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

function channelLuminance(value: number): number {
  const scaled = value / 255;
  return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([r, g, b]: Rgb): number {
  return (
    0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)
  );
}

export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(parseHex(a));
  const second = relativeLuminance(parseHex(b));
  const [lighter, darker] = first > second ? [first, second] : [second, first];
  return (lighter + 0.05) / (darker + 0.05);
}

function mix(from: Rgb, to: Rgb, amount: number): Rgb {
  return [
    from[0] + (to[0] - from[0]) * amount,
    from[1] + (to[1] - from[1]) * amount,
    from[2] + (to[2] - from[2]) * amount,
  ];
}

/**
 * Preto ou branco, o que for legível sobre `background`.
 *
 * Serve para o texto que fica em cima da cor de destaque: o rótulo do botão de
 * enviar, por exemplo.
 */
export function readableTextOn(background: string): string {
  return contrastRatio(background, LIGHT_INK) >= contrastRatio(background, DARK_INK)
    ? LIGHT_INK
    : DARK_INK;
}

/**
 * A cor de destaque puxada até ficar legível como texto sobre `surface`.
 *
 * Escurece contra fundo claro e clareia contra fundo escuro, parando assim que
 * alcança o mínimo da WCAG para texto normal. O tom original é preservado
 * sempre que já serve, então quem escolheu um roxo escuro não vê diferença.
 *
 * Quando nem o extremo alcança o mínimo (destaque cinza-médio sobre fundo
 * cinza-médio), devolve o extremo: é o ponto final da busca, o mais legível que
 * existe naquela direção, não um valor de consolo.
 */
export function readableInkOn(color: string, surface: string): string {
  if (contrastRatio(color, surface) >= AA_NORMAL_TEXT) {
    return color;
  }

  const origin = parseHex(color);
  const target = parseHex(relativeLuminance(parseHex(surface)) > 0.5 ? DARK_INK : LIGHT_INK);

  for (let amount = MIX_STEP; amount < 1; amount += MIX_STEP) {
    const candidate = toHex(mix(origin, target, amount));
    if (contrastRatio(candidate, surface) >= AA_NORMAL_TEXT) {
      return candidate;
    }
  }
  return toHex(target);
}
